/**
 * Permission card overlay — renders floating permission cards for
 * pending directory access requests.
 *
 * Used for OS-level directory permission consent.
 */

import { useEffect, useMemo } from "react";
import { useCanvasStore } from "@/stores/canvas-store";
import { useCompanionStore } from "@/stores/companion-store";
import { useOnboardingStore } from "@/stores/onboarding-store";
import { PermissionCard } from "@/components/layout/permission-card";
import { shouldSuppressNonOnboardingPermissions } from "@/lib/onboarding-permission-suppression";

export function PermissionCardOverlay() {
  const pendingPermissions = useCanvasStore((s) => s.pendingPermissions);
  const removePendingPermission = useCanvasStore(
    (s) => s.removePendingPermission,
  );
  const sendPermissionResolved = useCompanionStore(
    (s) => s.sendPermissionResolved,
  );
  const suppressPermissions = useOnboardingStore((s) =>
    shouldSuppressNonOnboardingPermissions({
      step: s.step,
      reconnecting: s.reconnecting,
    }),
  );

  const permissions = useMemo(
    () => Array.from(pendingPermissions.values()),
    [pendingPermissions],
  );

  useEffect(() => {
    if (!suppressPermissions || permissions.length === 0) return;
    for (const permission of permissions) {
      sendPermissionResolved?.(permission.executionId, true);
      removePendingPermission(permission.executionId);
    }
  }, [
    permissions,
    removePendingPermission,
    sendPermissionResolved,
    suppressPermissions,
  ]);

  if (permissions.length === 0 || suppressPermissions) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-40 z-[30000] flex flex-col items-center gap-3"
      data-testid="permission-card-overlay"
    >
      {permissions.map((perm) => (
        <div key={perm.executionId} className="pointer-events-auto">
          <PermissionCard
            executionId={perm.executionId}
            blockedDirectories={perm.blockedDirectories}
          />
        </div>
      ))}
    </div>
  );
}
